
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { MarketToken } from '../data/market-data';
import { ChartTimeframe } from '../types/charts';

interface TradingContextType {
    selectedPair: MarketToken | null;
    setSelectedPair: (token: MarketToken | null) => void;
    timeframe: ChartTimeframe;
    setTimeframe: (tf: ChartTimeframe) => void;
    isChartLoading: boolean;
}

const TradingContext = createContext<TradingContextType | undefined>(undefined);

export const useTrading = () => {
    const context = useContext(TradingContext);
    if (!context) {
        throw new Error('useTrading must be used within a TradingProvider');
    }
    return context;
};

export const TradingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [selectedPair, setSelectedPair] = useState<MarketToken | null>(null);
    const [timeframe, setTimeframe] = useState<ChartTimeframe>('1H');
    const [isChartLoading, setIsChartLoading] = useState(false);

    // Simulate loading state when pair changes
    useEffect(() => {
        if (selectedPair) {
            setIsChartLoading(true);
            const timer = setTimeout(() => setIsChartLoading(false), 800);
            return () => clearTimeout(timer);
        }
    }, [selectedPair, timeframe]);

    return (
        <TradingContext.Provider value={{
            selectedPair,
            setSelectedPair,
            timeframe,
            setTimeframe,
            isChartLoading
        }}>
            {children}
        </TradingContext.Provider>
    );
};
